import { useRouter } from 'next/router';

export interface UsePaginationOptions {
  param?: string;
  perPage: number;
  total: number;
  maxPages: number;
  renderEndPages?: boolean;
  renderStartPages?: boolean;
  totalPages?: number;
  currentPage?: number;
}

const getTotalPages = (total: number, perPage: number) => {
  return Math.ceil(total / perPage);
};

const getCurrentPage = (queryParam: string, totalPages: number) => {
  let currentPage = 1;

  if (queryParam && queryParam !== '') {
    const parsedParam = parseInt(queryParam, 10);

    if (parsedParam >= 1 && parsedParam <= totalPages) {
      return parsedParam;
    }
  }

  return currentPage;
};

const range = (start: number, end: number): number[] => {
  const length = end - start + 1;
  return Array.from({ length }, (_, i) => start + i);
};

/**
 * Modified version of: https://github.com/mui/material-ui/blob/master/packages/mui-material/src/usePagination/usePagination.js
 *
 * @param currentPage
 * @param maxPages
 * @param total
 * @param renderEndPages
 * @param renderStartPages
 */
const getPageList = (
  currentPage: number,
  maxPages: number = 9,
  total: number,
  renderEndPages: boolean = true,
  renderStartPages: boolean = true
): Array<number | string> => {
  const boundaryCount = 1;
  const siblingCount = Math.floor(maxPages / 2);
  const startPages = range(1, Math.min(boundaryCount, total));
  const endPages = range(
    Math.max(total - boundaryCount + 1, boundaryCount + 1),
    total
  );

  const siblingsStart = Math.max(
    Math.min(
      // Natural start
      currentPage - siblingCount,
      // Lower boundary when page is high
      total - boundaryCount - siblingCount * 2 - 1
    ),
    // Greater than startPages
    boundaryCount + 2
  );

  const siblingsEnd = Math.min(
    Math.max(
      // Natural end
      currentPage + siblingCount,
      // Upper boundary when page is low
      boundaryCount + siblingCount * 2 + 2
    ),
    // Less than endPages
    endPages.length > 0 ? endPages[0] - 2 : total - 1
  );

  // Basic list of items to render
  // e.g. itemList = ['previous', 1, 'ellipsis', 4, 5, 6, 'ellipsis', 10, 'next']
  return [
    'previous',
    ...(renderStartPages ? startPages : []),

    // Start ellipsis
    ...(siblingsStart > boundaryCount + 2
      ? ['ellipsis']
      : boundaryCount + 1 < total - boundaryCount
      ? [boundaryCount + 1]
      : []),

    // Sibling pages
    ...range(siblingsStart, siblingsEnd),

    // End ellipsis
    ...(siblingsEnd < total - boundaryCount - 1
      ? ['ellipsis']
      : total - boundaryCount > boundaryCount
      ? [total - boundaryCount]
      : []),

    ...(renderEndPages ? endPages : []),
    'next',
  ];
};

export default function usePagination({
  param = 'page',
  perPage,
  total,
  maxPages,
  renderStartPages,
  renderEndPages,
  totalPages,
  currentPage,
}: UsePaginationOptions) {
  const router = useRouter();

  const calculatedTotalPages = totalPages || getTotalPages(total, perPage);
  const calculatedCurrentPage =
    currentPage || getCurrentPage(router.query[param] as string, totalPages);
  const pageList = getPageList(
    calculatedCurrentPage,
    maxPages,
    calculatedTotalPages,
    renderEndPages,
    renderStartPages
  );

  return {
    currentPage,
    pageList,
    totalPages,
  };
}
